import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Stack,
  Grid,
  Paper,
  Skeleton,
  LinearProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Home,
  CalendarToday,
  AttachMoney,
  Hotel,
  Groups,
  EventAvailable,
  ShowChart,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PlatformDashboard from './platform/PlatformDashboard';

interface DashboardStats {
  properties: number;
  units: number;
  reservations: number;
  users: number;
  activeReservations: number;
  occupancyRate: number;
  todayRevenue: number;
  monthlyRevenue?: number;
  averageStayLength?: number;
  checkInsToday?: number;
  checkOutsToday?: number;
  lastUpdated: string;
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: number;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend, subtitle }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="600" sx={{ mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        {trend !== undefined && (
          <Box display="flex" alignItems="center" mt={2}>
            {trend >= 0 ? (
              <ArrowUpward sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
            ) : (
              <ArrowDownward sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
            )}
            <Typography 
              variant="caption" 
              color={trend >= 0 ? 'success.main' : 'error.main'}
              fontWeight="500"
            >
              {Math.abs(trend)}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const DashboardPage: React.FC = () => {
  const { user, tenantId } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<DashboardStats>({
    properties: 0,
    units: 0,
    reservations: 0,
    users: 0,
    activeReservations: 0,
    occupancyRate: 0,
    todayRevenue: 0,
    monthlyRevenue: 0,
    averageStayLength: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    lastUpdated: new Date().toISOString(),
  });

  useEffect(() => {
    if (tenantId) {
      fetchDashboardData();
    }
  }, [tenantId]);

  // If platform admin with no tenant selected, show platform dashboard
  if (user?.isPlatformAdmin && !tenantId) {
    return <PlatformDashboard />;
  }

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/reports/dashboard');
      if (response.data.success) {
        // Simulate additional data for demo
        const enhancedStats = {
          ...response.data.data,
          monthlyRevenue: (response.data.data.todayRevenue || 0) * 30,
          averageStayLength: 3.5,
          checkInsToday: Math.floor(Math.random() * 10) + 5,
          checkOutsToday: Math.floor(Math.random() * 8) + 3,
        };
        setStats(enhancedStats);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Dashboard
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back, {user?.firstName}! Here's what's happening with your properties today.
        </Typography>
      </Box>

      {/* Primary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`$${(stats.monthlyRevenue || 0).toLocaleString()}`}
            icon={<AttachMoney />}
            color={theme.palette.success.main}
            trend={12}
            subtitle="This month"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Occupancy Rate"
            value={`${stats.occupancyRate || 0}%`}
            icon={<Home />}
            color={theme.palette.primary.main}
            trend={5}
            subtitle={`${stats.activeReservations} of ${stats.units} units`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Reservations"
            value={stats.activeReservations || 0}
            icon={<Hotel />}
            color={theme.palette.info.main}
            trend={-3}
            subtitle="Currently checked in"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Available Units"
            value={stats.units > 0 ? (stats.units - stats.activeReservations) : 0}
            icon={<EventAvailable />}
            color={theme.palette.warning.main}
            subtitle="Ready for booking"
          />
        </Grid>
      </Grid>

      {/* Today's Activity */}
      <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
        Today's Activity
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" fontWeight="600">
                    {stats.checkInsToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check-ins Today
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="secondary" fontWeight="600">
                    {stats.checkOutsToday || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check-outs Today
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main" fontWeight="600">
                    ${(stats.todayRevenue || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Revenue
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main" fontWeight="600">
                    {stats.averageStayLength || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Stay (nights)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Properties
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {stats.properties}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={75} 
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Total Units
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {stats.units}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={85} 
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Total Guests
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {stats.users}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={60} 
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Performance Overview */}
      <Typography variant="h6" fontWeight="500" gutterBottom sx={{ mb: 2 }}>
        Performance Overview
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Revenue Trend</Typography>
              <ShowChart color="action" />
            </Box>
            <Box height={200} display="flex" alignItems="center" justifyContent="center">
              <Typography color="text.secondary">
                Revenue chart will be displayed here
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Occupancy Trend</Typography>
              <TrendingUp color="action" />
            </Box>
            <Box height={200} display="flex" alignItems="center" justifyContent="center">
              <Typography color="text.secondary">
                Occupancy chart will be displayed here
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Last Updated */}
      <Box mt={4} textAlign="center">
        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date(stats.lastUpdated).toLocaleString()}
        </Typography>
      </Box>
    </Container>
  );
};

export default DashboardPage;